import { AgentApi } from '@/domains/agent/apis';
import { PageModel } from '@/domains/seek/models/page-model';
import { PageDataService } from '@/domains/seek/services/page-data-service';
import { PageRepository } from '@/domains/seek/services/page-repository';
import type { AgentStreamMessage, PageInitInput, QuestionData, QuestionModuleData } from '@/domains/seek/types';
import { QuestionModule, QuestionModuleDataStatus, QuestionServerSideStatus } from '@/domains/seek/types';
import { AgentUnionCode } from '@/types/ai/agent';
import { Site, SLD } from '@patsnap-utils/biz';
import { OpenService } from '@patsnap/tahiti-core/services/open-service';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import _ from 'lodash';
import { PageApi } from '../../apis/page-api';
import type { PageInitService } from '../page-init-service';

@injectable()
export class PageInitServiceImpl implements PageInitService {
    constructor(
        @inject(PageRepository) private _pageRepository: PageRepository,
        @inject(PageModel) private _pageModel: PageModel,
        @inject(PageDataService) private _pageDataService: PageDataService,
        @inject(OpenService) private _openService: OpenService,
        @inject(AgentApi) private _agentApi: AgentApi,
        @inject(PageApi) private _pageApi: PageApi
    ) {}

    pageInit(initData: PageInitInput): Promise<QuestionData[]> {
        const requestSignal = new Date().getTime().toString();
        this._pageModel.update((s) => {
            return {
                ...s,
                _question_list: [],
                _init_loading: true,
                _is_init_finished: false,
                _request_signal: requestSignal,
            }
        })
        return this._pageRepository.pageInit(initData)
            .then(data => {
                const _is_init_finished = _.every(data, (question) => {
                    return _.every(question.module_data, (module) => {
                        return module.status === QuestionServerSideStatus.FINISH;
                    })
                })

                this._pageModel.update((s) => {
                    return {
                        ...s,
                        _init_loading: false,
                        _is_init_finished,
                    }
                })
                return this._pageDataService.processAllQuestionModuleData(data, requestSignal);
            })
    }

    toSeekAgent(appendData: string, questionId: string): Promise<void> {
        return this._pageApi.seekRewrite(questionId, appendData).then((res) => {
            return this._agentApi.quickChatJob({
                input: res,
                code: AgentUnionCode.eureka_rd_ai_agent_technical_qa,
            }).then(({ job_id }) => {
                job_id && this._openService.open(new Site({ sld: SLD.EUREKA, pathname: `ai/agent-flow/${job_id}` }).href)
            })
        })
    }

    stopPageStreamEvent(question_id: string):Promise<unknown> {
        return this._pageRepository.stopPageStreamEvent(question_id)
    }

    regenerateModules(question: QuestionData, module_names: QuestionModule[]): Promise<QuestionData[]> {
        const requestSignal = new Date().getTime().toString();
        this._pageModel.update((s) => {
            return {
                ...s,
                _request_signal: requestSignal,
            }
        })
        return this._pageDataService.reProcessPartialQuestionModuleData(question, module_names, requestSignal);
    }

    pageInitWithAgent(message: AgentStreamMessage) {
        const modules = message.modules || [QuestionModule.summary];
        const question = {
            module_data: [...modules?.map(ele => {
                let res = {
                    module_name: ele,
                    content: message?.[ele.toLowerCase()],
                    _module_status: message.status === QuestionServerSideStatus.FINISH
                        ? QuestionModuleDataStatus.success
                        : QuestionModuleDataStatus.streaming,
                };
                if (ele === QuestionModule.summary) {
                    res = {
                        ...res,
                        content: {
                            content: message?.summary,
                            think_content: message?.summary_think,
                            check_id_to_output: message?.check_id_to_output,
                            check_summary: message?.check_summary,
                            renderer: message?.renderer,
                        },
                        _module_status: message.status === QuestionServerSideStatus.FINISH
                            ? QuestionModuleDataStatus.success
                            : message?.summary_think || message?.summary
                                ? QuestionModuleDataStatus.streaming
                                : QuestionModuleDataStatus.starting,
                    }
                } else if (ele === QuestionModule.application) {
                    res = {
                        ...res,
                        content: JSON.stringify(message?.application),
                    }
                }
                return res as QuestionModuleData;
            }), {
                module_name: QuestionModule.reference,
                content: message?.references,
            }, {
                module_name: QuestionModule.project_item_list,
                content: message?.project_item_list,
            }],
            modules: modules?.filter(ele => ele !== QuestionModule.recommend && ele !== QuestionModule.reference),
            seek_id: message?.seek_id,
            question_id: message?.question_id,
            title: message.title,
        } as QuestionData
        this._pageModel.update((s) => {
            return {
                ...s,
                _question_list: [question],
                _init_loading: !message?.title,
                _in_agent: true,
            }
        })
    }
}
